import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "United Kingdom",
  firstName: "",
  lastName: "",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  phone: "",
  shippingMethod: "standard",
  cardNumber: "",
  expiry: "",
  cvc: "",
  cardName: "",
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const lines = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === item.id),
    }))
    .filter((item) => item.product);
  const subtotal = lines.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );
  const selectedRates =
    siteConfig.shippingRates[form.country] ||
    siteConfig.shippingRates["United Kingdom"];
  const standardShipping =
    subtotal >= siteConfig.shippingThreshold ? 0 : selectedRates.standard;
  const shipping =
    form.shippingMethod === "express"
      ? selectedRates.express
      : standardShipping;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === "cardNumber")
      nextValue = value
        .replace(/\D/g, "")
        .slice(0, 16)
        .replace(/(\d{4})(?=\d)/g, "$1 ");
    if (name === "expiry")
      nextValue = value
        .replace(/\D/g, "")
        .slice(0, 4)
        .replace(/^(\d{2})(\d)/, "$1/$2");
    if (name === "cvc") nextValue = value.replace(/\D/g, "").slice(0, 4);
    setForm((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      nextErrors.email = "Enter a valid email address.";
    [
      "firstName",
      "lastName",
      "address",
      "city",
      "postalCode",
      "cardName",
    ].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (form.cardNumber.replace(/\s/g, "").length !== 16)
      nextErrors.cardNumber = "Enter a 16 digit test card number.";
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(form.expiry)) {
      nextErrors.expiry = "Enter a valid future date in MM/YY format.";
    } else {
      const [month, year] = form.expiry.split("/").map(Number);
      const now = new Date();
      const expiryEnd = new Date(2000 + year, month, 0, 23, 59, 59);
      if (expiryEnd < now) nextErrors.expiry = "This test card has expired.";
    }
    if (!/^\d{3,4}$/.test(form.cvc)) nextErrors.cvc = "Enter 3 or 4 digits.";
    return nextErrors;
  };

  const placeOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      document.getElementById(Object.keys(nextErrors)[0])?.focus();
      return;
    }

    setSubmitting(true);
    const orderNumber = `IH-${Date.now().toString(36).slice(-7).toUpperCase()}`;
    clearCart();
    navigate("/paysuccess", {
      state: { orderNumber, total, email: form.email },
    });
  };

  if (lines.length === 0) {
    return (
      <main className="checkout-empty page-shell">
        <span className="eyebrow">Checkout</span>
        <h1>Your bag is empty</h1>
        <p>Add a product before starting checkout.</p>
        <Link className="button-primary" to="/shop">
          Browse products
        </Link>
      </main>
    );
  }

  const field = (name, label, options = {}) => (
    <label
      className={`checkout-field ${errors[name] ? "has-error" : ""}`}
      htmlFor={name}
    >
      <span>{label}</span>
      <input
        {...options}
        id={name}
        name={name}
        value={form[name]}
        onChange={updateField}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
      />
      {errors[name] && <small id={`${name}-error`}>{errors[name]}</small>}
    </label>
  );

  return (
    <main className="checkout-page">
      <div className="checkout-page__top page-shell">
        <Link to="/cartpage">Bag</Link>
        <span>/</span>
        <strong>Information</strong>
        <span>/</span>
        <span>Confirmation</span>
      </div>
      <form className="checkout-layout" onSubmit={placeOrder} noValidate>
        <div className="checkout-form">
          <section>
            <div className="checkout-form__heading">
              <span>01</span>
              <h1>Contact</h1>
            </div>
            {field("email", "Email address", {
              type: "email",
              autoComplete: "email",
              placeholder: "you@example.com",
            })}
          </section>

          <section>
            <div className="checkout-form__heading">
              <span>02</span>
              <h2>Delivery</h2>
            </div>
            <label className="checkout-field" htmlFor="country">
              <span>Country / region</span>
              <select
                id="country"
                name="country"
                value={form.country}
                onChange={updateField}
              >
                {Object.keys(siteConfig.shippingRates).map((country) => (
                  <option key={country}>{country}</option>
                ))}
              </select>
            </label>
            <div className="checkout-form__row">
              {field("firstName", "First name", { autoComplete: "given-name" })}
              {field("lastName", "Last name", { autoComplete: "family-name" })}
            </div>
            {field("address", "Address", { autoComplete: "street-address" })}
            {field("apartment", "Apartment, suite, etc. (optional)")}
            <div className="checkout-form__row">
              {field("city", "City", { autoComplete: "address-level2" })}
              {field("postalCode", "Postal code", {
                autoComplete: "postal-code",
              })}
            </div>
            {field("phone", "Phone (optional)", {
              type: "tel",
              autoComplete: "tel",
            })}
          </section>

          <section>
            <div className="checkout-form__heading">
              <span>03</span>
              <h2>Shipping method</h2>
            </div>
            <div className="shipping-methods">
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked={form.shippingMethod === "standard"}
                  onChange={updateField}
                />
                <span>
                  <strong>Tracked standard</strong>
                  <small>
                    {form.country === "United Kingdom" ? "2–4" : "4–10"} working
                    days
                  </small>
                </span>
                <b>
                  {standardShipping === 0
                    ? "Free"
                    : formatPrice(standardShipping)}
                </b>
              </label>
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="express"
                  checked={form.shippingMethod === "express"}
                  onChange={updateField}
                />
                <span>
                  <strong>Express courier</strong>
                  <small>
                    {form.country === "United Kingdom" ? "1–2" : "2–5"} working
                    days
                  </small>
                </span>
                <b>{formatPrice(selectedRates.express)}</b>
              </label>
            </div>
          </section>

          <section>
            <div className="checkout-form__heading">
              <span>04</span>
              <h2>Payment</h2>
            </div>
            <div className="checkout-form__notice">
              <FiLock /> Demo checkout — no payment will be processed.
            </div>
            <div className="payment-box">
              {field("cardNumber", "Card number", {
                inputMode: "numeric",
                autoComplete: "cc-number",
                placeholder: "4242 4242 4242 4242",
              })}
              {field("cardName", "Name on card", { autoComplete: "cc-name" })}
              <div className="checkout-form__row">
                {field("expiry", "Expiry", {
                  inputMode: "numeric",
                  autoComplete: "cc-exp",
                  placeholder: "MM/YY",
                })}
                {field("cvc", "Security code", {
                  inputMode: "numeric",
                  autoComplete: "cc-csc",
                  placeholder: "CVC",
                })}
              </div>
            </div>
          </section>

          <div className="checkout-form__actions">
            <Link to="/cartpage">← Return to bag</Link>
            <button
              className="button-primary"
              type="submit"
              disabled={submitting}
            >
              {submitting
                ? "Placing order…"
                : `Place order · ${formatPrice(total)}`}
            </button>
          </div>
        </div>

        <aside className="checkout-summary">
          <span className="eyebrow">Your order</span>
          <h2>{lines.reduce((sum, item) => sum + item.quantity, 0)} items</h2>
          <div className="checkout-summary__items">
            {lines.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div>
                  <img src={item.product.image} alt={item.product.name} />
                  <span>{item.quantity}</span>
                </div>
                <p>
                  <strong>{item.product.name}</strong>
                  <small>
                    {item.color} · {item.size}
                  </small>
                </p>
                <b>{formatPrice(item.product.price * item.quantity)}</b>
              </article>
            ))}
          </div>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatPrice(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>{formatPrice(total)}</dd>
            </div>
          </dl>
          <p>GBP · Taxes included</p>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
